/**
 * Renders a character's icon plus its display name. Colour comes from the `--char-*` token for the slot, but
 * colour is never the only indicator: the display name (and the slot's shape label, when known) is always
 * shown as text too.
 */
import { CHARACTER_SLOTS, type CharacterSlotId } from '@momentum/shared';
import { characterAssetUrl } from '../assets/index.ts';

export interface CharacterBadgeProps {
  slot: CharacterSlotId | null;
  displayName: string;
  size?: number;
  className?: string;
}

export function CharacterBadge({ slot, displayName, size = 48, className }: CharacterBadgeProps) {
  const appearance = slot ? CHARACTER_SLOTS[slot] : null;
  const assetSlot = appearance?.assetSlot ?? 'receiver';
  const colorVar = appearance ? `var(--${appearance.colorToken})` : 'var(--ink-muted)';

  return (
    <span
      className={className ? `character-badge ${className}` : 'character-badge'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          borderRadius: '999px',
          background: appearance ? `color-mix(in srgb, ${colorVar} 25%, white)` : 'var(--surface)',
          border: `2px solid ${colorVar}`,
          flexShrink: 0,
        }}
      >
        <img src={characterAssetUrl(assetSlot)} alt="" width={Math.round(size * 0.7)} height={Math.round(size * 0.7)} />
      </span>
      <span className="stack-sm" style={{ gap: 0 }}>
        <span style={{ fontWeight: 600 }}>{displayName}</span>
        {appearance && appearance.label !== displayName ? (
          <span className="text-muted" style={{ fontSize: '0.8em' }}>
            {appearance.label}
          </span>
        ) : null}
      </span>
    </span>
  );
}
